import type { PdfLayoutColumn } from "@repo/shared/types";
import { Button } from "@/components/ui/button";
import { ComponentCard } from "./component-card";
import type { PdfComponent, PdfComponentType } from "./types";

type LayoutColumnProps = {
  column: PdfLayoutColumn;
  title: string;
  components: PdfComponent[];
  loading: boolean;
  /** Something is being dragged. */
  isDragging: boolean;
  /** The dragged item may be dropped into a block (not a block, not a page break). */
  canDrop: boolean;
  draggedId: string | null;
  labels: {
    addComponent: string;
    dropComponentHere: string;
    noComponents: string;
    duplicateComponent: string;
    settings: string;
    delete: string;
    conditional: string;
  };
  getLabel: (type: PdfComponentType) => string;
  onAddComponent: () => void;
  /** Drop into this column; `index` inserts before that component. */
  onDrop: (e: React.DragEvent, index?: number) => void;
  onComponentDragStart: (e: React.DragEvent, component: PdfComponent, index: number) => void;
  onComponentDragEnd: () => void;
  onComponentSettings: (component: PdfComponent) => void;
  onComponentDelete: (component: PdfComponent) => void;
  onComponentDuplicate: (component: PdfComponent) => void;
};

/**
 * One column of a page block: a column of a 2-Column Block, or the single
 * column of a Same Page Block (QL-384). Components are dropped into it,
 * reordered within it and dragged out of it.
 */
export function LayoutColumn({
  column,
  title,
  components,
  loading,
  isDragging,
  canDrop,
  draggedId,
  labels,
  getLabel,
  onAddComponent,
  onDrop,
  onComponentDragStart,
  onComponentDragEnd,
  onComponentSettings,
  onComponentDelete,
  onComponentDuplicate,
}: LayoutColumnProps) {
  return (
    <div
      className="space-y-2 min-h-[100px] p-3 border-2 border-dashed rounded-lg"
      data-column={column}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-muted-foreground">{title}</h4>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onAddComponent();
          }}
          disabled={loading}
        >
          {labels.addComponent}
        </Button>
      </div>
      <div className="space-y-2">
        {components.length === 0 ? (
          // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag events
          <div
            onDragOver={(e) => {
              // Don't allow blocks or page breaks to be dropped here
              if (!canDrop) return;
              e.preventDefault();
              if (isDragging) {
                e.currentTarget.classList.add("border-primary", "bg-primary/5");
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("border-primary", "bg-primary/5");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-primary", "bg-primary/5");
              if (!canDrop) return;
              if (isDragging) onDrop(e);
            }}
            className="text-center py-8 text-xs text-muted-foreground border-2 border-dashed rounded-lg transition-all"
          >
            {isDragging && canDrop ? labels.dropComponentHere : labels.noComponents}
          </div>
        ) : (
          components.map((component, index) => {
            const isComponentDragging = draggedId === component.id;
            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div
              <div
                key={component.id}
                className={`${isComponentDragging ? "opacity-30" : ""} transition-opacity`}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (isDragging && !isComponentDragging && canDrop) {
                    e.currentTarget.classList.add("ring-2", "ring-primary");
                  }
                }}
                onDragOver={(e) => {
                  if (!canDrop) {
                    e.dataTransfer.dropEffect = "none";
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    e.currentTarget.classList.remove("ring-2", "ring-primary");
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove("ring-2", "ring-primary");
                  if (!canDrop) return;
                  if (isDragging) onDrop(e, index);
                }}
              >
                <ComponentCard
                  component={component}
                  onSettings={() => onComponentSettings(component)}
                  onDelete={() => onComponentDelete(component)}
                  onDuplicate={() => onComponentDuplicate(component)}
                  duplicateLabel={labels.duplicateComponent}
                  settingsLabel={labels.settings}
                  deleteLabel={labels.delete}
                  conditionalLabel={labels.conditional}
                  onDragStart={(e) => onComponentDragStart(e, component, index)}
                  onDragEnd={onComponentDragEnd}
                  getLabel={getLabel}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
